import type { AirdropConfig, ExtensionConfig, ExtensionType } from "@repo/shared-types";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

import { fadeInUp } from "../../lib/animations";
import { getNativeCurrencySymbol } from "../../lib/wagmi-config";
import { Input } from "../ui/Input";
import { AirdropAddressList } from "./AirdropAddressList";

/** Parse a string to integer, returning fallback (0) on NaN. */
function safeParseInt(value: string, fallback = 0): number {
	const parsed = Number.parseInt(value, 10);
	return Number.isNaN(parsed) ? fallback : parsed;
}

/** Parse a string to float, returning fallback (0) on NaN. */
function safeParseFloat(value: string, fallback = 0): number {
	const parsed = Number.parseFloat(value);
	return Number.isNaN(parsed) ? fallback : parsed;
}

type ExtensionFieldValue = string | number | Array<{ address: string; amount: number }>;

type ExtensionConfigSectionProps = {
	extensions: ExtensionConfig[];
	onExtensionsChange: (extensions: ExtensionConfig[]) => void;
};

type ExtensionToggle = {
	type: ExtensionType;
	title: string;
	description: string;
	icon: string;
	disabled: boolean;
	disabledReason: string;
};

const availableExtensions: ExtensionToggle[] = [
	{
		type: "Vault",
		title: "Token Vault",
		description: "Lock tokens with vesting schedule",
		icon: "🔒",
		disabled: false,
		disabledReason: "",
	},
	{
		type: "Airdrop",
		title: "Community Airdrop",
		description: "Distribute tokens to community members",
		icon: "🎁",
		disabled: false,
		disabledReason: "",
	},
	{
		type: "DevBuy",
		title: "Developer Buy",
		description: "Automated initial purchase by creator",
		icon: "💰",
		disabled: false,
		disabledReason: "",
	},
	{
		type: "Presale",
		title: "Token Presale",
		description: "Early access token sale period",
		icon: "⏰",
		disabled: false,
		disabledReason: "",
	},
];

const defaultConfigs: Record<ExtensionType, ExtensionConfig> = {
	Vault: { type: "Vault", percentage: 10, lockDurationDays: 30, vestingDurationDays: 0 },
	Airdrop: { type: "Airdrop", recipients: [], lockPeriodDays: 7, vestingDays: 0 },
	DevBuy: { type: "DevBuy", amount: 1.0 },
	Presale: {
		type: "Presale",
		minGoalEth: 1,
		maxGoalEth: 10,
		duration: 24,
		supplyAllocationPercent: 50,
		lockPeriodDays: 7,
		vestingDays: 0,
	},
};

export function ExtensionConfigSection({
	extensions,
	onExtensionsChange,
}: ExtensionConfigSectionProps) {
	const [expandedExtension, setExpandedExtension] = useState<ExtensionType | null>(null);

	const isExtensionEnabled = (type: ExtensionType) => {
		return extensions.some((ext) => ext.type === type);
	};

	const getExtensionConfig = (type: ExtensionType) => {
		return extensions.find((ext) => ext.type === type);
	};

	const toggleExtension = (type: ExtensionType) => {
		if (isExtensionEnabled(type)) {
			const newExtensions = extensions.filter((ext) => ext.type !== type);
			onExtensionsChange(newExtensions);
			if (expandedExtension === type) {
				setExpandedExtension(null);
			}
		} else {
			const newExtensions = [...extensions, defaultConfigs[type]];
			onExtensionsChange(newExtensions);
			setExpandedExtension(type);
		}
	};

	const updateExtensionConfig = (
		type: ExtensionType,
		field: string,
		value: ExtensionFieldValue
	) => {
		const newExtensions = extensions.map((ext): ExtensionConfig => {
			if (ext.type === type) {
				return { ...ext, [field]: value };
			}
			return ext;
		});
		onExtensionsChange(newExtensions);
	};

	const toggleConfigPanel = (type: ExtensionType) => {
		setExpandedExtension(expandedExtension === type ? null : type);
	};

	return (
		<div className="space-y-4">
			{availableExtensions.map((extension) => {
				const isEnabled = isExtensionEnabled(extension.type);
				const config = getExtensionConfig(extension.type);
				const isExpanded = expandedExtension === extension.type;
				const isDisabled = extension.disabled;

				return (
					<div
						key={extension.type}
						className={`border border-pastel-border rounded-lg overflow-hidden ${isDisabled ? "opacity-60" : ""}`}
					>
						{/* Extension Toggle */}
						<div className="p-4">
							<div className="flex items-center justify-between">
								<div className="flex items-center space-x-3">
									<span className="text-xl" aria-hidden="true">
										{extension.icon}
									</span>
									<div>
										<h4 className="font-medium text-pastel-black">{extension.title}</h4>
										<p className="text-sm text-pastel-gray">
											{isDisabled ? extension.disabledReason : extension.description}
										</p>
									</div>
								</div>

								<div className="flex items-center space-x-2">
									{isEnabled && !isDisabled && (
										<button
											type="button"
											onClick={() => toggleConfigPanel(extension.type)}
											aria-label={`${isExpanded ? "Collapse" : "Expand"} ${extension.title} config`}
											className="p-1 text-pastel-gray hover:text-pastel-black transition-colors"
										>
											<span
												className={`transform transition-transform inline-block ${
													isExpanded ? "rotate-180" : ""
												}`}
											>
												▼
											</span>
										</button>
									)}

									<button
										type="button"
										onClick={() => toggleExtension(extension.type)}
										disabled={isDisabled}
										className={`
											relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none
											${isDisabled ? "bg-pastel-light-gray cursor-not-allowed" : isEnabled ? "bg-pastel-vibrant-blue" : "bg-pastel-light-gray"}
										`}
										aria-label={`${isEnabled ? "Disable" : "Enable"} ${extension.title}`}
										title={isDisabled ? extension.disabledReason : ""}
									>
										<span
											className={`
												inline-block h-4 w-4 rounded-full bg-white shadow-lg transition-transform
												${isEnabled ? "translate-x-6" : "translate-x-1"}
											`}
										/>
									</button>
								</div>
							</div>
						</div>

						{/* Extension Config Panel */}
						<AnimatePresence>
							{isEnabled && isExpanded && config && !isDisabled && (
								<motion.div
									initial="hidden"
									animate="visible"
									exit="exit"
									variants={fadeInUp}
									className="border-t border-pastel-border bg-pastel-light-gray/50 p-4"
								>
									<ExtensionConfigForm
										config={config}
										onConfigChange={(field, value) =>
											updateExtensionConfig(extension.type, field, value)
										}
									/>
								</motion.div>
							)}
						</AnimatePresence>
					</div>
				);
			})}
		</div>
	);
}

type AirdropConfigFormPanelProps = {
	config: AirdropConfig;
	onConfigChange: (field: string, value: ExtensionFieldValue) => void;
};

function AirdropConfigFormPanel({ config, onConfigChange }: AirdropConfigFormPanelProps) {
	return (
		<div className="space-y-4">
			<AirdropAddressList
				recipients={config.recipients}
				onRecipientsChange={(recipients) => onConfigChange("recipients", recipients)}
			/>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				<Input
					label="Lock Period (days)"
					type="number"
					min="1"
					value={config.lockPeriodDays.toString()}
					onChange={(e) => onConfigChange("lockPeriodDays", safeParseInt(e.target.value, 1))}
					placeholder="7"
				/>
				<Input
					label="Vesting Period (days)"
					type="number"
					min="0"
					value={config.vestingDays.toString()}
					onChange={(e) => onConfigChange("vestingDays", safeParseInt(e.target.value))}
					placeholder="0"
				/>
			</div>
			<p className="text-xs text-pastel-gray">
				Tokens are locked during the lock period, then released linearly over the vesting period.
			</p>
		</div>
	);
}

type ExtensionConfigFormProps = {
	config: ExtensionConfig;
	onConfigChange: (field: string, value: ExtensionFieldValue) => void;
};

function ExtensionConfigForm({ config, onConfigChange }: ExtensionConfigFormProps) {
	const handleChange = (field: string, value: ExtensionFieldValue) => {
		onConfigChange(field, value);
	};

	switch (config.type) {
		case "Vault":
			return (
				<div className="space-y-4">
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
						<Input
							label="% of Token Supply"
							type="number"
							min="1"
							max="90"
							value={config.percentage.toString()}
							onChange={(e) => handleChange("percentage", safeParseInt(e.target.value, 1))}
							placeholder="10"
						/>
						<Input
							label="Lock Period (days)"
							type="number"
							min="7"
							value={config.lockDurationDays.toString()}
							onChange={(e) => handleChange("lockDurationDays", safeParseInt(e.target.value, 7))}
							placeholder="30"
						/>
						<Input
							label="Vesting Period (days)"
							type="number"
							min="0"
							value={config.vestingDurationDays.toString()}
							onChange={(e) => handleChange("vestingDurationDays", safeParseInt(e.target.value))}
							placeholder="0"
						/>
					</div>
					<p className="text-xs text-pastel-gray">
						Tokens are fully locked during the lock period, then released linearly over the vesting
						period.
					</p>
				</div>
			);

		case "DevBuy":
			return (
				<div className="grid grid-cols-1 gap-4">
					<Input
						label={`Purchase Amount (${getNativeCurrencySymbol()})`}
						type="number"
						min="0.01"
						step="0.01"
						value={config.amount.toString()}
						onChange={(e) => handleChange("amount", safeParseFloat(e.target.value, 0.01))}
						placeholder="1.0"
					/>
				</div>
			);

		case "Presale":
			return (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
					<Input
						label={`Min ${getNativeCurrencySymbol()} Goal`}
						type="number"
						min="0.01"
						step="0.01"
						value={config.minGoalEth.toString()}
						onChange={(e) => handleChange("minGoalEth", safeParseFloat(e.target.value, 0.01))}
						placeholder="1"
					/>
					<Input
						label={`Max ${getNativeCurrencySymbol()} Goal`}
						type="number"
						min="0.01"
						step="0.01"
						value={config.maxGoalEth.toString()}
						onChange={(e) => handleChange("maxGoalEth", safeParseFloat(e.target.value, 0.01))}
						placeholder="10"
					/>
					<Input
						label="Duration (hours)"
						type="number"
						min="1"
						value={config.duration.toString()}
						onChange={(e) => handleChange("duration", safeParseInt(e.target.value, 1))}
						placeholder="24"
					/>
					<Input
						label="Supply Allocation %"
						type="number"
						min="1"
						max="100"
						value={config.supplyAllocationPercent.toString()}
						onChange={(e) =>
							handleChange("supplyAllocationPercent", safeParseInt(e.target.value, 1))
						}
						placeholder="50"
					/>
					<Input
						label="Token Lock Period (days)"
						type="number"
						min="7"
						value={config.lockPeriodDays.toString()}
						onChange={(e) => handleChange("lockPeriodDays", safeParseInt(e.target.value, 7))}
						placeholder="7"
					/>
					<Input
						label="Vesting Period (days)"
						type="number"
						min="0"
						value={config.vestingDays.toString()}
						onChange={(e) => handleChange("vestingDays", safeParseInt(e.target.value))}
						placeholder="0"
					/>
				</div>
			);

		case "Airdrop":
			return <AirdropConfigFormPanel config={config} onConfigChange={handleChange} />;

		default:
			return null;
	}
}
